// client/src/pages/seo/QrCodeSpeisekarteOhneAppDe.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";

const sections = [
  { id: "answer", label: "Wie funktioniert eine QR-Speisekarte ohne App?" },
  { id: "scanner", label: "Braucht man einen separaten QR-Scanner?" },
  { id: "browser", label: "Browser-Menü oder Restaurant-App?" },
  { id: "requirements", label: "Was muss eine gute Browser-Speisekarte können?" },
  { id: "mistakes", label: "Typische Fehler" },
  { id: "faq", label: "FAQ" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuWithoutAppDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title = "QR-Code-Speisekarte ohne App: Menü im Browser | Best Place";
    setMeta(
      "description",
      "QR-Code-Speisekarte ohne App: Gäste scannen den Code mit dem Smartphone und öffnen das Menü direkt im Browser – ohne Download, Login oder Registrierung."
    );
    setCanonical("https://best-place.club/de/qr-code-speisekarte-ohne-app");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          de: "/de/qr-code-speisekarte-ohne-app",
          en: "/en/qr-code-menu-without-app",
          no: "/no/qr-kode-meny-uten-app",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR-Code-Speisekarte ohne App</span>
          </h1>
          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Kamera öffnen, QR-Code scannen, Link antippen und die Speisekarte im
                Browser ansehen. Mehr sollte ein Gast für das Öffnen eines Menüs
                nicht tun müssen.
              </p>
              <p className="seoHero__text">
                Diese Seite behandelt bewusst nur die Frage, wie ein QR-Menü ohne
                App funktioniert. Für Auswahl, Erstellung und Einsatz einer
                vollständigen QR-Speisekarte gibt es unsere Hauptseite.
              </p>
              <p>
                <Link to="/de/qr-code-speisekarte">Zur QR-Code-Speisekarte für Restaurants</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="articls">
        <h2>Inhalt</h2>
        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button type="button" className="qrArticle__navButton" onClick={() => scrollToSection(item.id)}>
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="answer" className="acquaintance">
          <h2>Wie funktioniert eine QR-Speisekarte ohne App?</h2>
          <p>
            Der QR-Code enthält beziehungsweise verweist auf einen normalen Weblink.
            Moderne Smartphones erkennen diesen Link nach dem Scan und bieten ihn
            direkt zum Öffnen an. Die Speisekarte wird anschließend wie eine mobile
            Webseite im Browser geladen.
          </p>
          <ol className="qrArticle__block-li">
            <li className="text__seo-li">Smartphone-Kamera öffnen.</li>
            <li className="text__seo-li">QR-Code auf dem Tisch scannen.</li>
            <li className="text__seo-li">Angezeigten Link antippen.</li>
            <li className="text__seo-li">Speisekarte direkt im Browser ansehen.</li>
          </ol>
          <p>
            Für Gäste entfallen App Store, Google Play, Kontoerstellung und Login.
          </p>
        </section>

        <section id="scanner" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Braucht man einen separaten QR-Scanner?</h2>
            <p>
              In der Regel nicht. Auf aktuellen iPhones und Android-Geräten kann
              die integrierte Kamera QR-Codes erkennen. Entscheidend ist deshalb
              weniger eine zusätzliche Scanner-App als ein sauber gedruckter Code
              und eine gut erreichbare Zielseite.
            </p>
          </div>
        </section>

        <section id="browser" className="acquaintance">
          <h2>Browser-Menü oder Restaurant-App?</h2>
          <div className="seoComparison">
            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Browser-Menü</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">sofort erreichbar</li>
                <li className="text__seo-li">kein Download</li>
                <li className="text__seo-li">gut für Erstbesucher</li>
                <li className="text__seo-li">einfach für den Tischzugang</li>
              </ul>
            </div>
            <div className="seoComparison__col">
              <h3>Eigene App</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">kann für Stammkunden sinnvoll sein</li>
                <li className="text__seo-li">möglich für Loyalty oder komplexe Funktionen</li>
                <li className="text__seo-li">zusätzliche Installation nötig</li>
                <li className="text__seo-li">für reine Menüanzeige oft unnötig</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="requirements" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was muss eine gute Browser-Speisekarte können?</h2>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">schnell auf dem Smartphone laden</li>
              <li className="text__seo-li">klare Kategorien zeigen</li>
              <li className="text__seo-li">Preise ohne Zoomen lesbar darstellen</li>
              <li className="text__seo-li">Fotos nur dort nutzen, wo sie helfen</li>
              <li className="text__seo-li">mehrere Sprachen unterstützen, wenn nötig</li>
              <li className="text__seo-li">ohne Login und Registrierung öffnen</li>
            </ul>
            <p>
              Die Browser-Frage ist nur ein Teil der Produktentscheidung. Wenn Sie
              eine komplette Lösung suchen, lesen Sie{" "}
              <Link to="/de/qr-code-speisekarte">QR-Code-Speisekarte für Restaurants</Link>.
            </p>
          </div>
        </section>

        <section id="mistakes" className="acquaintance">
          <h2>Typische Fehler bei QR-Menüs ohne App</h2>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Der QR-Code öffnet nur eine schwer lesbare PDF-Datei.</li>
            <li className="text__seo-li">Die mobile Seite lädt langsam oder hat zu kleine Schrift.</li>
            <li className="text__seo-li">Nach dem Scan wird trotzdem eine Registrierung verlangt.</li>
            <li className="text__seo-li">Der QR-Code wurde vor dem Druck nicht mit mehreren Geräten getestet.</li>
            <li className="text__seo-li">Neben dem Code steht nicht, was sich nach dem Scan öffnet.</li>
          </ul>
        </section>

        <section id="faq" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>FAQ zur QR-Code-Speisekarte ohne App</h2>
            <div className="seoFaq">
              <details>
                <summary>Funktioniert das auf dem iPhone?</summary>
                <p>Ja. Der QR-Code kann mit der Kamera erkannt und der Link im Browser geöffnet werden.</p>
              </details>
              <details>
                <summary>Funktioniert das auf Android?</summary>
                <p>Ja. Aktuelle Android-Geräte erkennen QR-Codes meist über Kamera oder Google Lens.</p>
              </details>
              <details>
                <summary>Kann auch ein PDF ohne App geöffnet werden?</summary>
                <p>Ja, aber ein PDF ist nicht automatisch mobilfreundlich. Für eine Speisekarte ist eine echte mobile Seite oft leichter zu lesen.</p>
              </details>
              <details>
                <summary>Muss ich eine eigene Restaurant-App entwickeln?</summary>
                <p>Für das reine Anzeigen der Speisekarte am Tisch normalerweise nicht.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Weiterführend</h2>
          <div className="seoCompareSmall">
            <p><Link to="/de/qr-code-speisekarte">QR-Code-Speisekarte für Restaurants</Link></p>
            <p><Link to="/de/qr-code-speisekarte-drucken">QR-Code drucken</Link></p>
            <p><Link to="/de/digitale-speisekarte">Digitale Speisekarte</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
